import React from 'react';
import { motion } from 'framer-motion';
import SiteCTA from '@/components/SiteCTA';
import { Sparkles, CheckCircle, ChevronDown, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const packages = [
  {
    name: 'Starter Session',
    price: '$100',
    priceDetail: 'One Session',
    tagline: 'Focused help on the most important issue right now.',
    features: [
      '60 minute one to one session.',
      'Focused problem solving to address the most important challenge right now.',
      'Confidential space to refine leadership style and skills.',
      'Rapid resume optimization.',
      'Immediate 14-day next step plan.',
      'Recorded session notes and action logs.',
    ],
  },
  {
    name: 'Growth Pack',
    originalPrice: '$300',
    discountedPrice: '$285',
    priceDetail: 'Three Sessions',
    tagline: 'Build momentum and a clear plan you can measure.',
    features: [
      'All Starter Session benefits.',
      '3 sessions, 60 minutes each within 30 to 45 days.',
      'LinkedIn polish.',
      'Co-created 90 day career plan with measurable outcomes.',
      'Email support between sessions.',
    ],
    footer: 'Save 5%. About $95 per session.',
    badge: 'Most Popular',
  },
  {
    name: 'Premier Accelerator',
    originalPrice: '$500',
    discountedPrice: '$450',
    priceDetail: 'Five Sessions',
    tagline: 'Advance faster with hands on practice, accountability, and career assets.',
    features: [
      'All Growth Pack benefits.',
      '5 sessions, 60 minutes each within 60 to 90 days.',
      'Portfolio of career assets templates you can reuse.',
      'Priority scheduling.',
      'Curated introductions to my network when appropriate after program completion.',
    ],
    footer: 'Save 10%. About $90 per session.',
  },
];

const howItWorks = [
  {
    title: 'Free Intro Call',
    description:
      'We meet, identify your goals, and I will provide you a 1-page overview of key takeaways, how I can help, and clear next steps.',
  },
  {
    title: 'Plan and Schedule',
    description:
      'If you commit, we will coordinate calendars, book your session(s), and align on payment.',
  },
  {
    title: 'Do The Work',
    description:
      'We practice, review progress, and adjust for real outcomes.',
  },
];

const audienceColumns = [
  {
    title: 'Who it is for?',
    items: [
      'PMO leaders and serious career builders.',
      'Project, program, and portfolio managers.',
      'High potential practitioners moving into leadership.',
      'Consultants seeking sharper client impact.',
    ],
  },
  {
    title: 'What You Will Practice:',
    items: [
      'Stakeholder management with role plays.',
      'Executive ready storytelling.',
      'Decision making under real constraints.',
      'Career navigation moves for PMO paths.',
    ],
  },
  {
    title: 'Integrity and Confidentiality:',
    items: [
      'Private sessions, NDA on request.',
      'No placement promises or pay to introduce.',
      'Introductions only when there is clear value for both sides.',
      'Honest feedback and measurable outcomes.',
    ],
  },
];

const CoachingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px]" />
        <motion.div
          className="absolute top-16 right-24 w-72 h-72 bg-gold/15 rounded-full blur-3xl"
          animate={{ x: [0, 20, 0], y: [0, -20, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-16 left-16 w-56 h-56 bg-amber-400/10 rounded-full blur-3xl"
          animate={{ x: [0, -15, 0], y: [0, 25, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="container mx-auto px-4 relative z-10 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-gold/20 to-amber-400/20 rounded-full px-6 py-3 mb-6 border border-gold/30 backdrop-blur-sm">
            <Sparkles className="w-5 h-5 text-gold" />
            <span className="text-gold font-bold text-sm uppercase tracking-wider">Project Leadership Coaching</span>
            <Sparkles className="w-5 h-5 text-gold" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold via-amber-300 to-gold">Grow Leaders.</span>
            <br />
            <span className="text-white">Strengthen Delivery. Raise the Bar.</span>
          </h1>
          <p className="text-xl text-gray-300 mt-6 max-w-3xl mx-auto">
            Build influence. Elevate your career. Solve real work challenges with a confidential partner who has led large PMO portfolios and teams.
          </p>
        </motion.div>
        <motion.div
          className="absolute bottom-8 inset-x-0 flex justify-center"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <div className="flex flex-col items-center gap-2 text-gold">
            <span className="text-sm uppercase tracking-wider">Scroll to explore</span>
            <ChevronDown className="w-6 h-6" />
          </div>
        </motion.div>
      </section>

      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-2xl mx-auto bg-gradient-to-br from-gray-900/70 to-black/70 border border-gold/20 rounded-3xl p-8 shadow-2xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex flex-col md:flex-row items-center md:items-center text-center md:text-left gap-8 md:gap-12">
              <div className="relative w-44 h-44 md:w-56 md:h-56 rounded-[32px] border border-gold/30 overflow-hidden shadow-2xl">
                <img
                  src="/ceo-profile.jpg"
                  alt="Noah R. McCloud headshot"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>
              <div className="w-full max-w-xl">
                <span className="text-sm font-semibold tracking-widest text-gold uppercase">Your Coach</span>
                <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">Noah R. McCloud</h2>
                <ul className="space-y-4 text-gray-200">
                  {[
                    '20+ years in PMO leadership across multiple industries.',
                    'Built complex global teams and portfolios.',
                    'Practical and direct coaching tailored to your role and desired career path.',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-gold mt-1 flex-shrink-0" />
                      <span className="text-base leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-black via-gray-900/40 to-black">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center max-w-2xl mx-auto mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-5 pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300">
              Choose a package that fits your goals
            </h2>
            <p className="text-gray-300 text-lg">Take advantage of the Founder Launch Pricing.</p>
            <div className="mt-10 flex justify-center">
              <Button
                asChild
                size="lg"
                className="bg-gradient-to-r from-gold to-amber-400 hover:from-amber-400 hover:to-gold text-black px-10 py-7 text-lg rounded-full shadow-2xl hover:shadow-gold/50 transition-all font-bold group"
              >
                <a href="https://cal.com/noah.mccloud/optigold-pmo-coaching" className="flex items-center gap-3">
                  Schedule a Discovery Call
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
            </div>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
            {packages.map((pkg) => (
              <motion.div
                key={pkg.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="relative rounded-3xl border border-gold/20 bg-gradient-to-br from-gray-900/70 to-black/70 p-8 shadow-xl"
              >
                {pkg.badge && (
                  <span className="absolute -top-3 right-6 px-4 py-1 rounded-full bg-gradient-to-r from-gold to-amber-400 text-black text-xs font-bold shadow-lg">
                    {pkg.badge}
                  </span>
                )}
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-block px-3 py-1 rounded-full bg-gradient-to-r from-gold/15 to-amber-400/15 border border-gold/40 text-gold text-xs font-semibold uppercase tracking-widest">
                    {pkg.name}
                  </span>
                  <span className="text-sm text-gray-300">{pkg.priceDetail}</span>
                </div>
                <div className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300 mb-3">
                  {pkg.discountedPrice ? (
                    <>
                      <span className="text-white line-through decoration-white decoration-[4px]">
                        {pkg.originalPrice}
                      </span>
                      <span className="mx-2 text-white">→</span>
                      <span>{pkg.discountedPrice}</span>
                    </>
                  ) : (
                    pkg.price
                  )}
                </div>
                <p className="text-gray-200 mb-6">{pkg.tagline}</p>
                <ul className="space-y-3 text-sm text-gray-300">
                  {pkg.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-gold mt-1 flex-shrink-0" />
                      <span className="text-base leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>
                {pkg.footer && (
                  <div className="mt-6 text-sm font-semibold text-gold/90">{pkg.footer}</div>
                )}
              </motion.div>
            ))}
          </div>

          <motion.div
            className="mt-10 rounded-3xl border border-gold/20 bg-gradient-to-r from-gold/10 to-amber-400/10 p-8 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-sm font-semibold text-gold uppercase tracking-widest mb-3">Optional add on or stand-alone service</p>
            <p className="text-lg text-gray-200 mb-2">
              Build a portfolio of career assets templates you can leverage for career credibility and use.
            </p>
            <p className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300">$300</p>
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How it works</h2>
            <p className="text-gray-300 text-lg">
              Clear steps that keep you focused on outcomes from the first conversation.
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-3">
            {howItWorks.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="rounded-3xl border border-gold/20 bg-gradient-to-br from-gray-900/70 to-black/70 p-8 shadow-lg"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-gold/20 to-amber-400/20 border border-gold/40 flex items-center justify-center text-gold font-bold mb-6 text-lg">
                  {index + 1}
                </div>
                <h3 className="text-xl font-semibold mb-4">{step.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-black via-gray-900/40 to-black">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Audience & Practice Areas</h2>
            <p className="text-gray-300 text-lg">Designed for leaders who want traction, credibility, and measurable delivery impact.</p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-3">
            {audienceColumns.map((column) => (
              <motion.div
                key={column.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="rounded-3xl border border-gold/20 bg-gradient-to-br from-gray-900/70 to-black/70 p-8 shadow-lg"
              >
                <h3 className="text-xl font-semibold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300">
                  {column.title}
                </h3>
                <ul className="space-y-4 text-sm text-gray-300">
                  {column.items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-gold mt-1" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <SiteCTA
        ctaHref="https://cal.com/noah.mccloud/optigold-pmo-coaching"
        ctaLabel="Book Your Intro Call"
      />
      <section className="py-8 bg-black">
        <div className="container mx-auto px-4">
          <p className="text-xs text-gray-500 text-center">
            No placement or outcomes are guaranteed. Coaching is guidance and practice. You own the decisions and results.
          </p>
        </div>
      </section>
    </div>
  );
};

export default CoachingPage;
